import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { profilAutomatikQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { Button, Card, CardPad } from './ui/primitives'

/**
 * **Die Profil-Automatik ein- und ausschalten** (Nils, 20.08.2026: *„mit Deaktivierungsoption in
 * den Einstellungen"*).
 *
 * ## Warum die Karte auch dann etwas sagt, wenn nichts passiert ist
 *
 * Weil das der Normalfall ist. Der Takt läuft alle zehn Minuten und überspringt jeden, für den
 * es nichts Neues gibt – „nichts zu tun (5 unverändert)" ist die häufigste Meldung und
 * gleichzeitig die einzige Auskunft darüber, dass die Automatik überhaupt lebt. Dieselbe
 * Überlegung wie bei der Nachfluss-Karte ([E-149](../../../docs/ENTSCHEIDUNGEN.md)): was von
 * selbst läuft, kann nicht melden, was es **nicht** tut.
 */
export function ProfilAutomatik() {
  const queryClient = useQueryClient()
  const { data: stand, isError } = useQuery(profilAutomatikQuery)
  const [meldung, setMeldung] = useState<string | null>(null)

  const aktualisieren = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ki', 'profilAutomatik'] })
    await queryClient.invalidateQueries({ queryKey: ['people'] })
  }

  const schalten = useMutation({
    mutationFn: (aktiv: boolean) => trpc.ki.profilAutomatikSetzen.mutate({ aktiv }),
    onSuccess: async (r) => {
      setMeldung(r.aktiv ? 'Automatik läuft.' : 'Automatik abgeschaltet.')
      await aktualisieren()
    },
  })

  const jetzt = useMutation({
    mutationFn: () => trpc.ki.profileJetzt.mutate(),
    onSuccess: async (r) => {
      setMeldung(r.meldung)
      await aktualisieren()
    },
  })

  /** Wer sie nicht sehen darf, sieht keine kaputte Karte, sondern gar keine. */
  if (isError || !stand) return null

  return (
    <Card className="mb-3">
      <CardPad className="text-[12.5px]">
        <div className="flex flex-wrap items-center gap-2">
          <b className="text-[13px]">Entwicklerprofile</b>
          <span
            className={`rounded-md px-[7px] py-[2.5px] text-[10px] font-semibold ${
              stand.aktiv ? 'bg-[#e7f3e7] text-[#2c6e2c]' : 'bg-[#f3f2ef] text-ink2'
            }`}
          >
            {stand.aktiv ? 'läuft alle 10 Minuten' : 'abgeschaltet'}
          </span>
          <span className="ml-auto flex items-center gap-1.5">
            <Button
              variant="ghost"
              disabled={jetzt.isPending || !stand.aktiv}
              onClick={() => {
                setMeldung(null)
                jetzt.mutate()
              }}
              title={
                stand.aktiv
                  ? 'Denselben Weg wie der Zeitgeber, nur sofort'
                  : 'Abgeschaltet – erst einschalten'
              }
            >
              {jetzt.isPending ? 'läuft …' : 'jetzt ableiten'}
            </Button>
            <Button
              variant="ghost"
              disabled={schalten.isPending}
              onClick={() => {
                setMeldung(null)
                schalten.mutate(!stand.aktiv)
              }}
            >
              {stand.aktiv ? 'abschalten' : 'einschalten'}
            </Button>
          </span>
        </div>

        <p className="mt-1 text-[11.5px] text-ink2">
          Leitet die Profile aus den <b>erledigten Aufgaben</b> ab – gezählt wird dem Bearbeiter,
          nicht dem, der den Status umlegt. Wer nichts Neues erledigt hat, wird übersprungen und
          kostet nichts.
        </p>

        {/**
         * **Der Stand des letzten Laufs, auch wenn er „nichts zu tun" lautet.** Ohne diese Zeile
         * wäre nicht zu unterscheiden, ob die Automatik nichts findet oder gar nicht läuft.
         */}
        <p className="mt-1 text-[11px] text-muted">
          {stand.letzterLauf
            ? `Zuletzt ${new Date(stand.letzterLauf).toLocaleString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })} · ${stand.letzteMeldung ?? '—'}`
            : 'Noch nicht gelaufen – der erste Takt kommt 90 Sekunden nach dem Start.'}
        </p>

        {meldung && <p className="mt-1 text-[11.5px] text-accent-deep">{meldung}</p>}

        {/**
         * **Wo die Profile stehen** – sonst sucht man sie in dieser Karte und findet nichts.
         * Sie gehören an die Person, nicht in eine Liste daneben.
         */}
        <p className="mt-1 text-[11px] text-muted">
          Zu sehen unter <b>Benutzer</b>, jeweils unter dem Namen – dort auch der Knopf „übernehmen
          &amp; bearbeiten".
        </p>
      </CardPad>
    </Card>
  )
}
